import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

const load = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState(() => load("anaya_cart", []));
  const [wishlist, setWishlist] = useState(() => load("anaya_wishlist", []));

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => { localStorage.setItem("anaya_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("anaya_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const addToCart = useCallback((product, qty = 1, variant = "") => {
    const key = variant ? `${product.id}|${variant}` : product.id;
    setCart((prev) => {
      const found = prev.find((i) => (i.key || i.id) === key);
      if (found) return prev.map((i) => ((i.key || i.id) === key ? { ...i, qty: Math.min(i.qty + qty, 20) } : i));
      return [...prev, { id: product.id, key, variant, name: product.name, price: product.price, image: product.images?.[0] || "", qty }];
    });
    toast.success(variant ? `Design ${variant} added to your bag` : "Added to your bag");
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) => (qty <= 0 ? prev.filter((i) => (i.key || i.id) !== key) : prev.map((i) => ((i.key || i.id) === key ? { ...i, qty: Math.min(qty, 20) } : i))));
  }, []);

  const removeFromCart = useCallback((key) => setCart((prev) => prev.filter((i) => (i.key || i.id) !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.some((p) => p.id === product.id);
      if (!exists) toast.success("Saved to wishlist");
      return exists ? prev.filter((p) => p.id !== product.id) : [...prev, { id: product.id, name: product.name, price: product.price, image: product.images?.[0] || "" }];
    });
  }, []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);

  const value = { settings, setSettings, categories, cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal, wishlist, toggleWishlist };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
